import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import ApiService from 'redux/services/api-service';
import {addResultCases} from 'utils/helpers';
import {PivotSourceRow} from 'components/pivot-table/pivot-types';

export type FeasibilityScope = 'items' | 'locations';

// The assumptions sit in a module with no imports of their own; re-exported here so callers that
// already reach for this slice do not need to know that.
export {DEFAULT_ASSUMPTIONS, derivedMetricFactors} from './assumptions';
export type {FeasibilityAssumptions} from './assumptions';

export interface FeasibilityQueryPayload {
  campaign_id: number;
  /** One or more. The API refuses to total several, so they must be broken out. */
  period_types: string[];
  location_identifier_type?: string | null;
  /** Scopes area-level data to the areas `dashboard.Area` knows for these retailers. */
  retailer_ids?: number[];
  /** Ordered; order decides the left-to-right row columns. */
  row_group_by: string[];
  /** Ordered; order decides the column header nesting, outermost first. */
  column_group_by: string[];
  metrics: string[];
  // The API supports a week_ending range, but nothing sets it: for RFP data every `latest_x_wks`
  // row shares the same week_ending, so there is nothing to narrow.
  start_date?: string | null;
  end_date?: string | null;
  week_ending?: string[];
  ordering?: string | null;
  limit?: number;
  offset?: number;
  /** Keyed by dimension name, e.g. `{brand: ['Kool-Aid'], zip_code: ['90210']}`. */
  filters?: Record<string, string[]>;
}

export interface FeasibilityMeta {
  campaign_id: number;
  sales_dataset_id: number;
  has_rfp_data: boolean;
  period_types: string[];
  /** Weeks each period type covers: 4, 13, 26 or 52. */
  weeks_per_period: Record<string, number>;
  /** Average Weekly denominator per period type. */
  divisors: Record<string, number>;
  week_counts: Record<string, number>;
  /** Which period type ranked the page, when several are broken out across columns. */
  ranking_period_type: string | null;
  /** What `column_totals` is grouped by: the column dimensions, plus period_type if it rows. */
  total_group_by: string[];
  /** What the Total column is split by, if anything. */
  total_column_split_by: string[];
  grand_total_split_by: string[];
  location_identifier_type: string | null;
  available_location_identifier_types: string[];
  retailer_ids: number[];
  /** The areas the chosen retailers contributed, or null when none was chosen. */
  retailer_areas: string[] | null;
  /** A company-wide aggregate chosen alongside the areas it covers, so those sales count twice. */
  aggregate_areas_selected: string[];
  metrics: string[];
  row_group_by: string[];
  column_group_by: string[];
  row_count: number;
  limit: number;
  offset: number;
  earliest_week_ending: string | null;
  latest_week_ending: string | null;
  /** Changes when the authoritative set of uploaded files does, i.e. the data moved under us. */
  claiming_files: {count: number; max_id: number | null};
}

export interface FeasibilityColumn extends Record<string, unknown> {
  divisor: number;
}

export interface FeasibilityResponse {
  meta: FeasibilityMeta;
  columns: FeasibilityColumn[];
  results: PivotSourceRow[];
  /** One aggregate per column group, over every row in the filtered set - not just this page. */
  column_totals: PivotSourceRow[];
  /** Each page row collapsed across its columns: the Total column. */
  row_totals: PivotSourceRow[];
  /** Everything collapsed both ways: the Total column's cell on the Grand Total row. */
  grand_totals: PivotSourceRow[];
}

export interface FeasibilityAttributeOptions {
  values: string[];
  /** Null when the attribute is too high-cardinality to enumerate. */
  total: number | null;
  truncated: boolean;
}

export interface FeasibilityFiltersResponse {
  meta: {
    campaign_id: number;
    sales_dataset_id: number;
    has_rfp_data: boolean;
    location_identifier_types: string[];
    periods: {
      period_type: string;
      earliest_week_ending: string | null;
      latest_week_ending: string | null;
      week_endings: string[];
    }[];
    max_option_values: number;
  };
  item_attributes: Record<string, FeasibilityAttributeOptions>;
  location_attributes: Record<string, FeasibilityAttributeOptions>;
  /**
   * Retailers whose known areas appear in this dataset. `areas` is the set that can be added up;
   * `aggregate_areas` cover those, so including one alongside them would count sales twice.
   */
  retailers: {
    id: number;
    name: string;
    area_count: number;
    areas: string[];
    aggregate_areas: string[];
  }[];
}

/**
 * One period type's coverage and base sales. `stores` and the `_avg_weekly` figures are null rather
 * than zero when nothing was reported: a store count is a stock measure, so "no file said how many"
 * is not "none", and a period with no weeks in range has no average to give.
 */
export interface FeasibilitySummaryPeriod {
  period_type: string;
  week_ending: string | null;
  /** Weeks one row of this period type covers: 1, 4, 13, 26 or 52. */
  weeks: number;
  /** The Average Weekly denominator: `weeks` times the weeks in range. */
  divisor: number;
  regions: number;
  stores: number | null;
  products: number;
  base_sales: number;
  base_sales_avg_weekly: number | null;
  base_units: number;
  base_units_avg_weekly: number | null;
}

/** What a `top_markets` limit ranks by. The projections are constant multiples of these. */
export type TopMarketsMetric = 'sales_dollars' | 'sales_units';

/** The summary's own scope: the page filters, plus how many markets to keep. */
export interface FeasibilitySummaryPayload extends FeasibilityScopePayload {
  /** Keep only the N best markets. Null or absent reads every market in scope. */
  top_markets?: number | null;
  top_markets_by?: TopMarketsMetric;
}

/** What the summary was narrowed to, alongside the pivot meta it shares. */
export interface TopMarketsMeta {
  top_markets: number | null;
  top_markets_by: TopMarketsMetric | null;
  /** The period type whose sales ranked the markets, held still across every period column. */
  top_markets_ranking_period_type: string | null;
  /** The chosen markets, in rank order. Null when no limit was asked for - not an empty list. */
  top_market_names: string[] | null;
  /** How many markets were in scope before the limit, so the UI can say "10 of 36". */
  markets_available: number | null;
}

export interface FeasibilitySummaryResponse {
  /** The pivot meta minus everything about axes, totals and pagination, plus this view's own. */
  meta: Omit<
    FeasibilityMeta,
    | 'ranking_period_type'
    | 'total_group_by'
    | 'total_column_split_by'
    | 'grand_total_split_by'
    | 'metrics'
    | 'row_group_by'
    | 'column_group_by'
    | 'row_count'
    | 'limit'
    | 'offset'
  > &
    TopMarketsMeta;
  /**
   * One entry per requested period type, ordered by window length. Never collapsed into a total:
   * the windows nest, so adding them counts the same sales more than once.
   */
  results: FeasibilitySummaryPeriod[];
}

export type FeasibilityResult =
  | {as: 'idle'}
  | {as: 'loading'; requestId: string}
  | {as: 'error'; error: object}
  | ({as: 'success'} & FeasibilityResponse);

export type FeasibilityFiltersResult =
  | {as: 'idle'}
  | {as: 'loading'; requestId: string}
  | {as: 'error'; error: object}
  | ({as: 'success'} & FeasibilityFiltersResponse);

export type FeasibilitySummaryResult =
  | {as: 'idle'}
  | {as: 'loading'; requestId: string}
  | {as: 'error'; error: object}
  | ({as: 'success'} & FeasibilitySummaryResponse);

/** What the whole page is scoped to. The summary reads the same scope; it just has no axes. */
export type FeasibilityScopePayload = Omit<
  FeasibilityQueryPayload,
  'row_group_by' | 'column_group_by' | 'metrics' | 'ordering' | 'limit' | 'offset'
>;

/**
 * The page-wide scope, shared by the pivot and summary serializers.
 *
 * Filter values go as repeated parameters because attribute values contain commas
 * ("Kraft Foods, Inc."). Note `withFilterURLParams` cannot be used here - it emits `key__in=a,b`
 * via `params.set`.
 */
const appendScopeParams = (params: URLSearchParams, payload: FeasibilityScopePayload): URLSearchParams => {
  params.set('campaign_id', String(payload.campaign_id));
  params.set('period_type', payload.period_types.join(','));

  if (payload.location_identifier_type) {
    params.set('location_identifier_type', payload.location_identifier_type);
  }
  if (payload.retailer_ids?.length) params.set('retailer_id', payload.retailer_ids.join(','));
  if (payload.start_date) params.set('start_date', payload.start_date);
  if (payload.end_date) params.set('end_date', payload.end_date);

  (payload.week_ending ?? []).forEach(week => params.append('week_ending', week));
  Object.entries(payload.filters ?? {}).forEach(([field, values]) =>
    values.forEach(value => params.append(field, value)),
  );
  return params;
};

/**
 * The summary takes the page scope plus its own market limit. `top_markets` is omitted when unset
 * rather than sent as 0: the API reads absence as "every market", and the two must not diverge.
 */
const serializeSummaryParams = (payload: FeasibilitySummaryPayload): URLSearchParams => {
  const params = appendScopeParams(new URLSearchParams(), payload);
  if (payload.top_markets) {
    params.set('top_markets', String(payload.top_markets));
    if (payload.top_markets_by) params.set('top_markets_by', payload.top_markets_by);
  }
  return params;
};

/** Ordered field lists go as comma-joined values because their order is meaningful. */
const serializeFeasibilityParams = (payload: FeasibilityQueryPayload): URLSearchParams => {
  const params = appendScopeParams(new URLSearchParams(), payload);
  params.set('row_group_by', payload.row_group_by.join(','));
  params.set('column_group_by', payload.column_group_by.join(','));
  params.set('metrics', payload.metrics.join(','));

  if (payload.ordering) params.set('ordering', payload.ordering);
  if (payload.limit !== undefined) params.set('limit', String(payload.limit));
  if (payload.offset !== undefined) params.set('offset', String(payload.offset));
  return params;
};

export interface IState {
  itemsGrouped: FeasibilityResult;
  locationsGrouped: FeasibilityResult;
  filterOptions: FeasibilityFiltersResult;
  summary: FeasibilitySummaryResult;
}

export const rfpFeasibilityEndpoints = {
  getItemsGrouped: createAsyncThunk(
    'rfpFeasibility/getItemsGrouped',
    async (payload: FeasibilityQueryPayload, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(
          `/rfp_feasibility/items/?${serializeFeasibilityParams(payload).toString()}`,
        );
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
  getLocationsGrouped: createAsyncThunk(
    'rfpFeasibility/getLocationsGrouped',
    async (payload: FeasibilityQueryPayload, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(
          `/rfp_feasibility/locations/?${serializeFeasibilityParams(payload).toString()}`,
        );
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
  getSummary: createAsyncThunk(
    'rfpFeasibility/getSummary',
    async (payload: FeasibilitySummaryPayload, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(
          `/rfp_feasibility/summary/?${serializeSummaryParams(payload).toString()}`,
        );
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
  getFilterOptions: createAsyncThunk(
    'rfpFeasibility/getFilterOptions',
    async (campaignId: number, {rejectWithValue}) => {
      try {
        const response = await ApiService.get(`/rfp_feasibility/filters/?campaign_id=${campaignId}`);
        return response.data;
      } catch (error: any) {
        return rejectWithValue(error.response?.data);
      }
    },
  ),
};

export const rfpFeasibilitySlice = createSlice({
  name: 'rfpFeasibility',
  initialState: {
    itemsGrouped: {as: 'idle'},
    locationsGrouped: {as: 'idle'},
    filterOptions: {as: 'idle'},
    summary: {as: 'idle'},
  } as IState,
  reducers: {},
  extraReducers: builder => {
    // `addResultCases` drops responses whose requestId is stale, which matters here: changing the
    // pivot fires a new query while the previous one is still in flight.
    addResultCases(builder, rfpFeasibilityEndpoints.getItemsGrouped, 'itemsGrouped');
    addResultCases(builder, rfpFeasibilityEndpoints.getLocationsGrouped, 'locationsGrouped');
    addResultCases(builder, rfpFeasibilityEndpoints.getFilterOptions, 'filterOptions');
    addResultCases(builder, rfpFeasibilityEndpoints.getSummary, 'summary');
  },
});

export const rfpFeasibilityActions = rfpFeasibilitySlice.actions;

export type RfpFeasibilitySlice = {
  [rfpFeasibilitySlice.name]: ReturnType<(typeof rfpFeasibilitySlice)['reducer']>;
};
